import React, { useCallback, useEffect, useRef, useState } from 'react';

import useUsers from '../../context/UsersContext';
import useConfig from '../../context/ConfigContext';

import { Box } from '@mui/material';
import { sxConfig } from './ProductionGenerator.style';
import ProductionGeneratorTable from './ProductionGeneratorTable';
import ProductionGeneratorTree from './ProductionGeneratorTree';
import ProductionGeneratorNewProdTable from './ProductionGeneratorNewProdTable';
import useParam from '../../context/ParamContext';
import ProductionGeneratorGroupNewProdTable from './ProductionGeneratorGroupNewProdTable';
import prodgeneratorService from '../../service/prodgenerator.service';

const orderSourceKey = (row) => `${row.docTypeId}/${row.docId}/${row.lineNum}`;

const ProductionGeneratorPage = () => {
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const groupEnabled = params?.PRODUCTION_GENERATOR_GROUP_ENABLED === 'Y';
  const NewProdTable = groupEnabled ? ProductionGeneratorGroupNewProdTable : ProductionGeneratorNewProdTable;

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [removedItem, setRemovedItem] = useState(null);
  const [refetchTable, setRefetchTable] = useState(false);
  const [sourceData, setSourceData] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);
  const [groupSourceState, setGroupSourceState] = useState({ ready: false, sourceKeys: new Set() });
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const rowRefreshState = useRef({ revision: 0, updates: new Map() });
  const refreshOrderRows = useCallback(async (sources, signal) => {
    const rows = await prodgeneratorService.getOrderRows(sources, signal);
    if (signal.aborted) return;
    const refreshed = new Map(rows.map((row) => [orderSourceKey(row), row]));
    const sourceKeys = new Set(sources.map((source) => `${source.baseDocType}/${source.baseId}/${source.baseLine}`));
    const revision = ++rowRefreshState.current.revision;
    sourceKeys.forEach((key) => rowRefreshState.current.updates.set(key, { revision, row: refreshed.get(key) }));
    setReports((previous) => {
      const nextRows = previous.rows.flatMap((row) => {
        const key = orderSourceKey(row);
        return sourceKeys.has(key) ? refreshed.has(key) ? [refreshed.get(key)] : [] : [row];
      });
      return { rows: nextRows, rowCount: previous.rowCount - (previous.rows.length - nextRows.length) };
    });
  }, []);

  useEffect(() => {
    getParam();
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
  }, []);

  return (
    <Box sx={sxConfig.containerBox}>
      <Box sx={sxConfig.formBox}>
        <ProductionGeneratorTable
          reportsState={{ reports, setReports, rowRefreshState }}
          groupSourceState={groupEnabled ? groupSourceState : null}
          selectedRowsState={{ selectedRows, setSelectedRows }}
          selectedItemsState={{ selectedItems, setSelectedItems }}
          removedItemState={{ removedItem, setRemovedItem }}
          refetchState={{ refetchTable, setRefetchTable }}
          treeDataState={{ treeData, setTreeData }}
          sourceDataState={{ sourceData, setSourceData }}
          expandedItemsState={{ expandedItems, setExpandedItems }}
        />
        <ProductionGeneratorTree
          sourceDataState={{ sourceData, setSourceData }}
          treeDataState={{ treeData, setTreeData }}
          expandedItemsState={{ expandedItems, setExpandedItems }}
        />
        <NewProdTable
          refreshOrderRows={refreshOrderRows}
          setGroupSourceState={setGroupSourceState}
          removedItemState={{ removedItem, setRemovedItem }}
          refetchState={{ refetchTable, setRefetchTable }}
          selectedItemsState={{ selectedItems, setSelectedItems }}
          selectedRowsState={{ selectedRows, setSelectedRows }}
          sourceDataState={{ sourceData, setSourceData }}
          treeDataState={{ treeData, setTreeData }}
        />
      </Box>
    </Box>
  );
};

export default ProductionGeneratorPage;
